import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

export const Palette = ({ colors = [], size = "h-7 w-7" }) => (
  <div className="flex flex-wrap gap-1.5">
    {colors.map((c, i) => (
      <span key={i} title={`${c.name} ${c.hex}`} className={`${size} rounded-full border border-[var(--charcoal)]/15`} style={{ background: c.hex }} />
    ))}
  </div>
);

export const Directions = ({ directions, selectedId, onSelect, busyId }) => (
  <div className="grid gap-4 lg:grid-cols-3" data-testid="directions-list">
    {directions.map((d, i) => {
      const sel = d.id === selectedId;
      return (
        <article
          key={d.id}
          data-testid={`direction-card-${i}`}
          className={`flex flex-col rounded-[3px] border p-5 transition-colors duration-300 sm:p-6 ${sel ? "border-[var(--charcoal)] bg-[var(--bone-2)]" : "border-[var(--line)] bg-white/40 hover:border-[var(--charcoal)]/40"}`}
        >
          <div className="font-display text-sm text-[var(--accent)]">Direction {["I", "II", "III"][i]}</div>
          <h3 className="mt-1 font-display text-2xl leading-tight" data-testid={`direction-name-${i}`}>{d.name}</h3>
          <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]" data-testid={`direction-summary-${i}`}>{d.summary}</p>
          <div className="mt-4" data-testid={`direction-palette-${i}`}><Palette colors={d.palette} /></div>
          <div className="mt-4 text-xs leading-relaxed text-[var(--stone-dark)]" data-testid={`direction-materials-${i}`}>{d.materials.join(" · ")}</div>
          <div className="mt-auto pt-5">
            <button className={sel ? "btn-primary w-full" : "btn-ghost w-full"} onClick={() => onSelect(d.id)} disabled={!!busyId} data-testid={`direction-select-${i}`}>
              {busyId === d.id ? <Loader2 size={15} className="animate-spin" /> : sel ? <Check size={15} /> : null}
              {sel ? "Selected direction" : "Choose this direction"}
            </button>
          </div>
        </article>
      );
    })}
  </div>
);

export const BriefForm = ({ spec, onSave, saving }) => {
  const b = spec?.brief || {};
  const [instructions, setInstructions] = useState(b.instructions || "");
  const [budget, setBudget] = useState(b.budget || "");
  const [priorities, setPriorities] = useState((b.priorities || []).join(", "));
  const [preferences, setPreferences] = useState((b.preferences || []).join(", "));
  useEffect(() => {
    setInstructions(b.instructions || "");
    setBudget(b.budget || "");
    setPriorities((b.priorities || []).join(", "));
    setPreferences((b.preferences || []).join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec?.updatedAt]);
  const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  return (
    <form
      data-testid="brief-form"
      className="grid gap-4"
      onSubmit={(e) => { e.preventDefault(); onSave({ instructions, budget, priorities: split(priorities), preferences: split(preferences) }); }}
    >
      <label className="grid gap-1.5">
        <span className="eyebrow">Your brief</span>
        <textarea className="field" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Keep the original crown molding, warm oak and brass, brighter kitchen" data-testid="brief-instructions-input" />
      </label>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="grid gap-1.5"><span className="eyebrow">Budget</span><input className="field" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="$25,000" data-testid="brief-budget-input" /></label>
        <label className="grid gap-1.5"><span className="eyebrow">Priorities</span><input className="field" value={priorities} onChange={(e) => setPriorities(e.target.value)} placeholder="Kitchen, light" data-testid="brief-priorities-input" /></label>
        <label className="grid gap-1.5"><span className="eyebrow">Design preferences</span><input className="field" value={preferences} onChange={(e) => setPreferences(e.target.value)} placeholder="Oak, brass, limewash" data-testid="brief-preferences-input" /></label>
      </div>
      <div><button className="btn-primary" disabled={saving} data-testid="brief-save-button">{saving && <Loader2 size={15} className="animate-spin" />} Save brief</button></div>
    </form>
  );
};

const Row = ({ label, children, testid }) => (
  <div className="grid gap-1 border-t border-[var(--line)] py-3 sm:grid-cols-[10rem_1fr] sm:gap-6" data-testid={testid}>
    <div className="eyebrow pt-0.5">{label}</div>
    <div className="text-sm leading-relaxed">{children}</div>
  </div>
);

export const DesignSpecCard = ({ spec, testid = "design-spec-card" }) => {
  if (!spec || (!spec.name && !spec.brief)) {
    return <p data-testid={`${testid}-empty`} className="text-sm text-[var(--stone-dark)]">No design specification yet — choose a direction or write a brief.</p>;
  }
  const b = spec.brief || {};
  return (
    <div data-testid={testid}>
      {spec.name && <h3 className="font-display text-2xl" data-testid={`${testid}-direction`}>{spec.name}</h3>}
      {spec.summary && <p className="mt-1 text-sm text-[var(--ink-soft)]">{spec.summary}</p>}
      <div className="mt-4">
        {spec.palette?.length > 0 && <Row label="Palette"><Palette colors={spec.palette} size="h-6 w-6" /></Row>}
        {spec.materials?.length > 0 && <Row label="Materials">{spec.materials.join(" · ")}</Row>}
        {b.budget && <Row label="Budget" testid={`${testid}-budget`}>{b.budget}</Row>}
        {b.priorities?.length > 0 && <Row label="Priorities" testid={`${testid}-priorities`}>{b.priorities.join(", ")}</Row>}
        {b.preferences?.length > 0 && <Row label="Preferences" testid={`${testid}-preferences`}>{b.preferences.join(", ")}</Row>}
        {spec.preservationNotes?.length > 0 && <Row label="Preserve" testid={`${testid}-preservation`}>{spec.preservationNotes.join("; ")}</Row>}
        {b.instructions && <Row label="Brief" testid={`${testid}-instructions`}>{b.instructions}</Row>}
      </div>
    </div>
  );
};
